/* ==========================================================================
   nl-modal.js — one shared, event-delegated modal behavior for nl-scope
   sections. No per-instance init (delegation survives Shopify section reloads).

   Markup contract:
     <button data-nl-modal-trigger aria-controls="THE_MODAL_ID"> … </button>
     <div id="THE_MODAL_ID" data-nl-modal aria-hidden="true" role="dialog" aria-modal="true">
       <div data-nl-modal-close></div>          <!-- overlay (click to close) -->
       <div> … <button data-nl-modal-close>×</button> … </div>
     </div>
   Open state is the `data-open` attribute (CSS shows the modal on [data-open]).
   Closes on: close/overlay click, Escape. Locks body scroll while open,
   restores focus to the trigger on close.

   A trigger may carry data-nl-modal-video="<src>" to hand a source to the
   modal's <video data-nl-modal-player> on open, and the player is paused and
   detached on close so a hidden modal never keeps downloading or playing.
   Sections with one modal per item therefore need one modal, not N.

   Optional guards — each no-ops unless the attribute is present, so existing
   callers are unaffected:
     - trigger [aria-expanded]      — kept in sync with open/closed state.
     - [data-nl-modal-close-above="960"] on the MODAL — closes it when the
       viewport grows past that width. For panels that only exist below a
       breakpoint (a mobile nav drawer), where resizing to desktop would
       otherwise leave the panel open over the desktop layout.
   A left/right sliding drawer is this same behavior with different CSS on
   [data-open] — it is not a second module.

   TPM build-1 (additive): the open state is mirrored as `data-nl-open`, which
   is what this module (and other scripts) query; `data-open` stays for the CSS
   of every section that already styles it. Delegated listeners only, so a
   section reload needs no re-init — the load listener below is a documented
   no-op for the editor-lifecycle contract.
   ========================================================================== */
(function () {
  'use strict';

  function setExpanded(trigger, state) {
    if (trigger && trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', state ? 'true' : 'false');
    }
  }

  function open(modal, trigger) {
    if (!modal) return;
    modal.__nlReturn = document.activeElement;
    modal.__nlTrigger = trigger || null;
    setExpanded(modal.__nlTrigger, true);
    modal.setAttribute('data-open', '');
    modal.setAttribute('data-nl-open', '');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Optional shared video player: the trigger supplies the source.
    var player = modal.querySelector('[data-nl-modal-player]');
    if (player && trigger) {
      var src = trigger.getAttribute('data-nl-modal-video');
      var poster = trigger.getAttribute('data-nl-modal-poster');
      if (poster) player.setAttribute('poster', poster);
      if (src && player.getAttribute('src') !== src) {
        player.setAttribute('src', src);
        player.load();
      }
      var playAttempt = player.play();
      if (playAttempt && playAttempt.catch) playAttempt.catch(function () {});
    }

    var closer = modal.querySelector('[data-nl-modal-close]');
    if (closer && closer.focus) closer.focus();
  }

  function close(modal) {
    if (!modal) return;
    modal.removeAttribute('data-open');
    modal.removeAttribute('data-nl-open');
    modal.setAttribute('aria-hidden', 'true');
    setExpanded(modal.__nlTrigger, false);
    if (!document.querySelector('[data-nl-modal][data-nl-open]')) document.body.style.overflow = '';

    // Stop and detach any player so a closed modal downloads nothing.
    var player = modal.querySelector('[data-nl-modal-player]');
    if (player) {
      try { player.pause(); } catch (e) {}
      player.removeAttribute('src');
      try { player.load(); } catch (e) {}
    }

    var ret = modal.__nlReturn;
    if (ret && ret.focus) ret.focus();
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var trigger = e.target.closest('[data-nl-modal-trigger]');
    if (trigger) {
      var id = trigger.getAttribute('aria-controls');
      var modal = id && document.getElementById(id);
      if (modal) { e.preventDefault(); open(modal, trigger); }
      return;
    }
    var closer = e.target.closest('[data-nl-modal-close]');
    if (closer) { close(closer.closest('[data-nl-modal]')); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openModal = document.querySelector('[data-nl-modal][data-nl-open]');
    if (openModal) close(openModal);
  });

  // Breakpoint guard: close a panel that only exists below a given width once
  // the viewport grows past it. No-op for every modal without the attribute.
  window.addEventListener('resize', function () {
    var panels = document.querySelectorAll('[data-nl-modal][data-nl-open][data-nl-modal-close-above]');
    for (var i = 0; i < panels.length; i++) {
      var above = parseInt(panels[i].getAttribute('data-nl-modal-close-above'), 10);
      if (above && window.innerWidth > above) close(panels[i]);
    }
  }, { passive: true });

  // Delegation survives a section reload: nothing to re-init.
  document.addEventListener('shopify:section:load', function () {});

  // If a section is removed in the editor while its modal is open, release the
  // lock — and stop any player inside it.
  document.addEventListener('shopify:section:unload', function (e) {
    var stale = e.target && e.target.querySelector && e.target.querySelector('[data-nl-modal][data-nl-open]');
    if (stale) close(stale);
    if (!document.querySelector('[data-nl-modal][data-nl-open]')) document.body.style.overflow = '';
  });
})();
